import React, { useEffect, useState } from "react";
import TopBar from "@/components/TopBar";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, UserCheck, Play, Square, Activity, Check } from "lucide-react";
import CopyTradeModal from "@/components/CopyTradeModal";
import { useI18n } from "@/contexts/I18nContext";

export default function SocialTrading() {
  const { t } = useI18n();
  const [leaders, setLeaders] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeader, setSelectedLeader] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ldrs, fllws] = await Promise.all([
        api.get("/social/leaders"),
        api.get("/social/following")
      ]);
      setLeaders(ldrs.data);
      setFollowing(fllws.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUnfollow = async (leaderId) => {
    try {
      await api.post(`/social/unfollow/${leaderId}`);
      toast.success(t("social_unfollow"));
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const isFollowing = (leaderId) => {
    return following.some(f => f.leader_id === leaderId);
  };

  return (
    <div className="min-h-screen bg-app text-white">
      <TopBar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex items-center gap-3 mb-8">
          <Users className="w-8 h-8 text-buy" />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("social_title")}</h1>
        </div>

        {/* Following Section */}
        {following.length > 0 && (
          <div className="mb-12">
            <h2 className="text-sm uppercase tracking-widest text-dim font-bold mb-4 flex items-center gap-2">
              <UserCheck className="w-4 h-4" /> {t("social_following")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {following.map((f) => (
                <div key={f.leader_id} className="bg-panel border border-buy/30 rounded-md p-5 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-2 h-full bg-buy/20" />
                  <div className="font-mono text-lg mb-1">{f.display_name}</div>
                  <div className="text-xs text-dim uppercase tracking-widest mb-4">{t("social_active_follow")}</div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] text-dim uppercase tracking-widest">{t("social_per_trade")}</div>
                      <div className="font-mono text-buy">${f.amount_per_trade.toFixed(2)}</div>
                    </div>
                    <button 
                      onClick={() => handleUnfollow(f.leader_id)}
                      className="bg-sell/10 text-sell border border-sell/20 px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-bold hover:bg-sell/20 transition-colors flex items-center gap-1"
                    >
                      <Square className="w-3 h-3" /> {t("social_unfollow")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Leaders Section */}
        <div>
          <h2 className="text-sm uppercase tracking-widest text-dim font-bold mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4" /> {t("social_top_leaders")}
          </h2>
          
          <div className="bg-panel border border-line rounded-sm overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-dim text-sm uppercase tracking-widest">{t("loading")}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left min-w-[600px]">
                  <thead className="bg-app border-b border-line">
                    <tr className="text-[10px] text-dim uppercase tracking-widest">
                      <th className="px-4 py-3">{t("social_leader")}</th>
                      <th className="px-4 py-3 text-center">{t("social_followers_count")}</th>
                      <th className="px-4 py-3 text-center">{t("social_total_trades")}</th>
                      <th className="px-4 py-3 text-center">{t("social_win_rate")}</th>
                      <th className="px-4 py-3 text-right">{t("social_trade_action")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaders.map((ldr) => (
                      <tr key={ldr.id} className="border-b border-subtle last:border-0 hover:bg-panel-hover transition-colors">
                        <td className="px-4 py-4 font-mono text-sm">{ldr.display_name}</td>
                        <td className="px-4 py-4 text-center font-mono text-muted">{ldr.followers}</td>
                        <td className="px-4 py-4 text-center font-mono text-muted">{ldr.total_trades}</td>
                        <td className="px-4 py-4 text-center font-mono">
                          <span className={ldr.win_rate >= 50 ? "text-buy" : "text-sell"}>
                            %{ldr.win_rate.toFixed(1)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right">
                          {isFollowing(ldr.id) ? (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-buy border border-buy/30 bg-buy/10 px-2 py-1 rounded-sm">
                              <Check className="w-3 h-3" /> {t("social_following_label")}
                            </span>
                          ) : (
                            <button
                              onClick={() => {
                                if (ldr.copy_enabled === false) {
                                  toast.error("Bu kullanıcı işlemlerinin kopyalanmasına izin vermemiştir");
                                } else {
                                  setSelectedLeader(ldr);
                                }
                              }}
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold bg-buy text-black px-3 py-1.5 rounded-sm hover:bg-emerald-400 transition-colors"
                            >
                              <Play className="w-3 h-3" /> {t("social_copy")}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {leaders.length === 0 && (
                      <tr><td colSpan="5" className="px-4 py-8 text-center text-dim">{t("social_no_leaders")}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {selectedLeader && (
        <CopyTradeModal 
          leader={selectedLeader} 
          onClose={() => setSelectedLeader(null)} 
          onSuccess={fetchData} 
        />
      )}
    </div>
  );
}
